import { StatusCodes } from "http-status-codes";

import { MutationException, UnauthorizedException } from "@eisox/api";

import { m } from "../paraglide/messages";
import type { ErrorDialogState, MutationToastMeta, ToastAction, ToastPhase } from "./mutation-feedback.type";

const SUPPORT_MAIL = "support@eisox.com";

/** Messages are referenced, never called here: each one resolves the locale when it runs. */
const ACTION_MESSAGES = {
  create: { loading: m.creating, success: m.created },
  update: { loading: m.saving, success: m.saved },
  delete: { loading: m.deleting, success: m.deleted },
} satisfies Record<ToastAction, Record<Exclude<ToastPhase, "error">, () => string>>;

const toastIdFor = (mutationId: number, suffix = ""): string => `mutation-feedback-${mutationId}${suffix}`;

/** The meta wins over the wording implied by `action`, which the error phase does not vary on. */
const toastMessage = (toastMeta: MutationToastMeta | undefined, phase: ToastPhase): string =>
  toastMeta?.[phase] ??
  (phase === "error" ? m.error_unknown() : ACTION_MESSAGES[toastMeta?.action ?? "update"][phase]());

const descriptionForStatus = (status: StatusCodes | undefined): string => {
  switch (status) {
    case StatusCodes.BAD_REQUEST:
      return m.error_bad_request();
    case StatusCodes.FORBIDDEN:
      return m.error_forbidden();
    case StatusCodes.NOT_FOUND:
      return m.error_not_found();
    case undefined:
      return m.error_unknown();
    default:
      return m.error_internal_server_error();
  }
};

/** `DefaultError` lies at runtime: a `mutationFn` may reject with anything. */
const rejectionMessage = (rejection: unknown): string => {
  if (rejection instanceof Error) return rejection.message;
  if (typeof rejection === "string") return rejection;
  return String(rejection);
};

/** `null` keeps the failure silent — a revoked session already navigates away. */
const describeError = (rejection: unknown): ErrorDialogState | null => {
  if (rejection instanceof UnauthorizedException) return null;

  if (rejection instanceof MutationException) {
    return { status: rejection.requestInfo.status, message: rejection.message, requestInfo: rejection.requestInfo };
  }

  return { message: rejectionMessage(rejection) };
};

const supportMailtoHref = (app: string, { status, message, requestInfo }: ErrorDialogState): string => {
  const subject = encodeURIComponent(m.mail_subject({ app }));
  const body = encodeURIComponent(
    m.mail_body({
      url: requestInfo?.url ?? "",
      method: requestInfo?.method ?? "",
      status: status === undefined ? "" : String(status),
      args: requestInfo?.args ?? "",
      message,
    }),
  );

  return `mailto:${SUPPORT_MAIL}?subject=${subject}&body=${body}`;
};

export { describeError, descriptionForStatus, SUPPORT_MAIL, supportMailtoHref, toastIdFor, toastMessage };
